import { createFileRoute, Outlet, useRouterState } from "@tanstack/react-router";
import { z } from "zod";

import { AccountSettingsFlowProvider } from "../iam/account/hooks/useAccountSettingsFlow";
import { useSettingsFlow } from "../iam/account/hooks/useSettingsFlow";
import { IamPageContainer } from "../iam/shared/components/IamPageContainer";
import { VerifyEmailBanner } from "../iam/shared/components/VerifyEmailBanner";

/**
 * SETTINGS_FLOW_PATH matches the three account subpages that drive an Ory settings
 * flow (profile, security, preferences). The bare index does NOT match, so the layout
 * leaves the flow disabled there and nothing bounces.
 */
const SETTINGS_FLOW_PATH = /\/account\/(profile|security|preferences)$/;

/**
 * Route is /account: the account-settings layout, nested under _app so it inherits
 * the auth guard + the app shell. Its URL matches resolveNavSection's IAM prefixes,
 * so the shell sidenav slides to content B here. It frames every account subpage in
 * the shared IAM page container (the single width + rhythm strategy); the subpages
 * render inside the Outlet. The index redirects to the profile subpage.
 *
 * The layout — not the individual pages — owns the ONE Ory settings flow. It stays
 * mounted across child navigation, so the flow it mints survives a move between the
 * settings pages, shared through AccountSettingsFlowProvider. A cold open of any
 * settings subpage mints the flow over AJAX and stays on that URL — no bounce, no
 * ?flow= written. The ?flow= search is still honored for the post-registration
 * "complete your profile" landing (/account/profile?flow=), where the layout loads
 * that flow by id.
 */
export const Route = createFileRoute("/_app/account")({
  component: AccountLayout,
  validateSearch: z.object({ flow: z.string().optional() }),
});

function AccountLayout() {
  const search = Route.useSearch();

  // enabled is derived from the active child route during render — pure, no effect.
  // It is true only on the three settings-flow subpages, so the flow stays idle on the
  // bare index and never bounces there.
  const enabled = useRouterState({
    select: (state) => SETTINGS_FLOW_PATH.test(state.location.pathname),
  });

  const settings = useSettingsFlow({ enabled, flowId: search.flow });

  return (
    <>
      {/* The verify-email notice mounts OUTSIDE the page container so its row spans the
          content column edge to edge; the container below restores the page gutter for
          the routed body. It is the only notice the account raises: nothing here is
          billable — every billable thing belongs to an organization — so the billing
          notices live on the organization layout alone. */}
      <VerifyEmailBanner />
      <IamPageContainer>
        <AccountSettingsFlowProvider value={settings}>
          <Outlet />
        </AccountSettingsFlowProvider>
      </IamPageContainer>
    </>
  );
}
